import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'quaternary'
  | 'taskboard';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface Palette {
    quaternary: {
      main: string;
      light: string;
      lighter: string;
      darker: string;
      dark: string;
      blue: string;
      darkBlue: string;
      lightGreen: string;
      darkGreen: string;
      success: string;
      customTurquoise: string;
      dodgerBlue: string;
      linkColor: string;
      bottleGreen: string;
      navyBlue: string;
      lightBlue: string;
      ceruleanBlue: string;
      royalBlue: string;
    };
    taskboard: {
      deepPurple: string;
      orangeSpice: string;
      salmonPink: string;
      lightGreen: string;
      lightPeach: string;
      lightLavender: string;
      lightPurple: string;
      burntOrange: string;
      lightPink: string;
      celery: string;
      carissma: string;
      tealBlue: string;
      oliveGreen: string;
      mysticMaroon: string;
      lightRed: string;
      aliceBlue: string;
      darkSlateGray: string;
      raisinBlack: string;
      lighterdRed: string;
    };
    tertiary: {
      light: string;
    };
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#BFEBFA',
  light: '#89DAF5',
  main: '#3AC1EF',
  dark: '#11A3D4',
  darker: '#0D7FA5',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#CBA69F',
  light: '#A36457',
  main: '#7C210F',
  dark: '#57170B',
  darker: '#320D06',
  contrastText: '#FFFFFF',
};

const TERTIARY = {
  lighter: '#BDA5CC',
  light: '#8B62A6',
  main: '#591F80',
  dark: '#3E165A',
  darker: '#3E165A',
  contrastText: '#FFFFFF',
};
const QUATERNARY = {
  lighter: '#FFCC99',
  light: '#0080FF',
  main: '#0066CC',
  dark: '#004C99',
  darker: '#008B8B',
  contrastText: '#FFFFFF',
  success: '#28A3AA',
  blue: '#6AAEE0',
  darkBlue: '#4E7BBF',
  lightGreen: '#84CACE',
  darkGreen: '#28A2AA',
  customTurquoise: '#4EE2EA',
  ceruleanBlue: '#308fe8',
  linkColor: '#0000ee',
  bottleGreen: '#406673',
  navyBlue: '#5EACC8',
  lightBlue: '#CBE6F0',
  royalBlue: '#4E7BBF',
};

const INFO = {
  lighter: '#BFEBFA',
  light: '#89DAF5',
  main: '#3AC1EF',
  dark: '#11A3D4',
  darker: '#0D7FA5',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#D8FBDE',
  light: '#86E8AB',
  main: '#36B37E',
  dark: '#1B806A',
  darker: '#0A5554',
  contrastText: '#FFFFFF',
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFCEC7',
  light: '#FF9282',
  main: '#E34932',
  dark: '#942B1B',
  darker: '#63160A',
  contrastText: '#FFFFFF',
};

const TASKBOARD = {
  deepPurple: '#44546f',
  orangeSpice: '#FEA362',
  salmonPink: '#F87168',
  lightGeen: '#BAF3DB',
  lightPeach: '#FEDEC8',
  lightLavender: '#DFD8FD',
  lightPurple: '#9F8FEF',
  burntOrange: '#C25100',
  lightPink: '#FDD0EC',
  celery: '#94C748',
  carissma: '#E774BB',
  tealBlue: '#227D9B',
  oliveGreen: '#5B7F24',
  mysticMaroon: '#AE4787',
  lightRed: '#F96F6F',
  aliceBlue: '#E1F4FB',
  darkSlateGray: '#49454E',
  raisinBlack: '#4A454E',
  lighterdRed: '#FF9090',
};

const COMMON = {
  common: { black: '#000000', white: '#FFFFFF' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  tertiary: TERTIARY,
  quaternary: QUATERNARY,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  taskboard: TASKBOARD,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: { paper: '#FFFFFF', default: '#FFFFFF', neutral: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.16),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  } as const;

  return themeMode === 'light' ? light : dark;
}
